import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, from, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { SessionTokenService } from './session-token.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private readonly AUTH_ENDPOINTS = [
    `${environment.backendUrl}auth/sign-in`,
    `${environment.backendUrl}auth/refresh`,
    `${environment.backendUrl}auth/sign-out`,
  ];

  constructor(private sessionTokenService: SessionTokenService) {}

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    if (!request.url.startsWith(environment.backendUrl)) {
      return next.handle(request);
    }

    if (this.AUTH_ENDPOINTS.includes(request.url)) {
      return next.handle(request.clone({ withCredentials: true }));
    }

    // Public backend endpoints must remain callable without a session. Private
    // services already request a token through AuthService before sending.
    if (!this.sessionTokenService.getAccessToken()) {
      return next.handle(request);
    }

    return from(this.sessionTokenService.ensureValidAccessToken()).pipe(
      switchMap((accessToken) =>
        next.handle(this.withAccessToken(request, accessToken)).pipe(
          catchError((error) => {
            if (!this.isAuthenticationError(error)) {
              return throwError(error);
            }

            return from(
              this.sessionTokenService.ensureValidAccessToken(true)
            ).pipe(
              switchMap((refreshedAccessToken) =>
                next.handle(
                  this.withAccessToken(request, refreshedAccessToken)
                )
              )
            );
          })
        )
      )
    );
  }

  private withAccessToken(
    request: HttpRequest<unknown>,
    accessToken: string
  ): HttpRequest<unknown> {
    return request.clone({
      setHeaders: { Authorization: `Bearer ${accessToken}` },
    });
  }

  private isAuthenticationError(error: unknown): boolean {
    if (!(error instanceof HttpErrorResponse)) return false;
    if (error.status === 401) return true;

    return (
      error.status === 403 &&
      typeof error.error?.message === 'string' &&
      error.error.message.includes('Invalid token')
    );
  }
}
